/**
 * In-page word highlighter for read-along / karaoke mode. When audio
 * starts with word timings, this class wraps each spoken word in the
 * user's selection in a span and toggles a `.lw-w-active` class on the
 * one whose [t0, t1] interval covers the current audio playhead.
 *
 * Previous version rendered a separate popover docked to the pill —
 * users found it distracting because their eye had to jump between
 * the selection in the page and the panel in the corner. The new
 * model highlights words directly in-page so the reading line of
 * sight stays put.
 *
 * Constraints + behaviour:
 *
 *  - We only wrap when the selection sits inside a single text node
 *    (overwhelmingly the common case for paragraph-prose). Multi-node
 *    selections — across links, formatting, list items — fall back to
 *    no in-page highlight rather than risk corrupting host-page DOM.
 *
 *  - The browser's blue selection band is cleared on show() because
 *    the active-word highlight is the new "where you are" indicator
 *    and two visual selections would compete.
 *
 *  - On hide() we put the original text node back in its original
 *    position, deleting every node we inserted. Restoration is
 *    reference-safe: the original textNode object is retained the
 *    whole time, so the host page's references / observers reattach
 *    cleanly.
 *
 *  - The SDK gives us words after Welsh normalisation (abbreviations
 *    expanded, numerals spelled out). We linear-search for each word
 *    string in the selected text starting from the previous match
 *    position. Mismatches (e.g. "Dr." in page → "Doctor" from SDK)
 *    silently skip that word — the audio still plays, the word just
 *    isn't highlighted in-page.
 */

export type WordTiming = {
  word: string;
  t0: number;
  t1: number;
  kind: 'word' | 'punctuation';
};

type WrapResult = {
  spans: HTMLSpanElement[];
  restore: () => void;
};

const HOST_STYLE_ID = 'llais-inpage-style';

/**
 * Inject the in-page highlight stylesheet into the HOST page's <head>
 * once. The spans we inject live in light DOM, so shadow-DOM CSS can't
 * style them. We scope everything under .lw-w + .lw-w.active and keep
 * specificity high enough to win against most host page text rules
 * without going full !important.
 */
function ensureHostStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HOST_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HOST_STYLE_ID;
  // Two-layer model the user asked for:
  //   - Every wrapped word + punctuation gets a soft violet background
  //     ("still highlighted" — replaces the browser's blue selection band).
  //   - The currently-spoken word gets a bolder violet + colour shift so
  //     the eye can track it against the surrounding soft band.
  // Padding is tight (1px) so adjacent tokens flow as continuous prose,
  // and negative-margin compensates so the line height doesn't bulge.
  style.textContent = `
    .lw-w, .lw-p {
      background: rgba(109, 78, 226, 0.14);
      border-radius: 3px;
      padding: 1px 2px;
      margin: 0 -1px;
      transition: color 140ms cubic-bezier(0.16, 1, 0.3, 1),
                  background 140ms cubic-bezier(0.16, 1, 0.3, 1),
                  text-shadow 140ms cubic-bezier(0.16, 1, 0.3, 1);
    }
    .lw-w.active {
      color: #ffffff !important;
      background: linear-gradient(135deg, #7c3aed, #4f46e5);
      text-shadow: 0 0 12px rgba(124, 58, 237, 0.35);
      font-weight: 600;
    }
    @media (prefers-color-scheme: dark) {
      .lw-w, .lw-p { background: rgba(167, 139, 250, 0.20); }
      .lw-w.active {
        color: #ffffff !important;
        background: linear-gradient(135deg, #a78bfa, #818cf8);
        text-shadow: 0 0 14px rgba(167, 139, 250, 0.45);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .lw-w, .lw-p { transition: none !important; }
    }
  `;
  document.head.appendChild(style);
}

export class InPageHighlighter {
  private spans: HTMLSpanElement[] = [];
  private words: WordTiming[] = [];
  private activeIndex = -1;
  private restoreFn: (() => void) | null = null;

  show(range: Range, words: WordTiming[]): void {
    this.hide();
    // Diagnostic hook for in-browser debugging. Dumps the inputs +
    // outputs of the wrap step to window.__lwdiag so we can see why
    // a particular range/words combo produces 0 spans without
    // patching network calls.
    try {
      (window as unknown as { __lwdiag?: unknown }).__lwdiag = {
        ts: Date.now(),
        rangeText: range?.toString().slice(0, 100),
        rangeStartContainer: range?.startContainer?.nodeName,
        rangeStartOffset: range?.startOffset,
        rangeEndContainer: range?.endContainer?.nodeName,
        rangeEndOffset: range?.endOffset,
        commonAncestor: range?.commonAncestorContainer?.nodeName,
        wordsCount: words?.length,
        wordsFirst: words?.slice(0, 6).map((w) => w.word),
      };
    } catch {
      /* ignore */
    }
    if (!range || words.length === 0) return;
    ensureHostStyle();
    this.words = words;
    const wrap = wrapRangeInWordSpans(range, words);
    try {
      const diag = (window as unknown as { __lwdiag?: { wrapResult?: unknown } }).__lwdiag;
      if (diag) diag.wrapResult = { spans: wrap.spans.length };
    } catch {
      /* ignore */
    }
    if (wrap.spans.length === 0) return;
    this.spans = wrap.spans;
    this.restoreFn = wrap.restore;
    // Clear the browser's selection band — the .lw-w soft band CSS now
    // provides the "still highlighted" feel, and stacking the browser's
    // blue on top of our violet looks muddy. The Selection object itself
    // is reset, so further mouseup events evaluate fresh.
    try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
  }

  hide(): void {
    this.activeIndex = -1;
    if (this.restoreFn) {
      try { this.restoreFn(); } catch { /* host page may have mutated under us */ }
      this.restoreFn = null;
    }
    this.spans = [];
    this.words = [];
  }

  update(currentTimeS: number): void {
    if (this.spans.length === 0) return;
    const t = currentTimeS * 1000;

    // "Latest started word" is the right model: between two consecutive
    // words there's typically a 50-200ms silence; the previous strict
    // [t0, t1) check would un-highlight everything during those gaps and
    // the user saw a flicker / "only some words light up" effect.
    let activeWordIdx = -1;
    for (let i = 0; i < this.words.length; i++) {
      const w = this.words[i];
      if (w && w.t0 <= t) activeWordIdx = i;
      else break;
    }
    if (activeWordIdx < 0) {
      if (this.activeIndex >= 0) {
        this.spans[this.activeIndex]?.classList.remove('active');
        this.activeIndex = -1;
      }
      return;
    }

    // Each span carries an inclusive [from, to] range of SDK word indices.
    // Single-word spans have from == to. Group spans (one page-side token
    // covering an expanded sequence, e.g. "1917" → un mil naw cant un deg
    // saith) have from < to. Active span is the one whose range contains
    // activeWordIdx. Punctuation spans (.lw-p) are still rendered (so the
    // soft band continues over commas/periods) but never become the active
    // word — skip them in the lookup.
    let activeSpan: HTMLSpanElement | null = null;
    for (const span of this.spans) {
      if (!span.classList.contains('lw-w')) continue;
      const from = Number(span.dataset.from);
      const to = Number(span.dataset.to);
      if (activeWordIdx >= from && activeWordIdx <= to) {
        activeSpan = span;
        break;
      }
    }
    // Fallback: if no span's range covers activeWordIdx (e.g. the SDK
    // emitted a word that lives in a gap we couldn't fill — rare with
    // findAllGapTokens in place, but possible if the gap had no word-like
    // content at all), freeze on the most recent span that ended at or
    // before activeWordIdx so the highlight doesn't pop off mid-sentence.
    if (!activeSpan) {
      for (let i = this.spans.length - 1; i >= 0; i--) {
        const span = this.spans[i];
        if (!span || !span.classList.contains('lw-w')) continue;
        if (Number(span.dataset.to) <= activeWordIdx) {
          activeSpan = span;
          break;
        }
      }
    }

    const nextIndex = activeSpan ? this.spans.indexOf(activeSpan) : -1;
    if (nextIndex === this.activeIndex) return;
    if (this.activeIndex >= 0) this.spans[this.activeIndex]?.classList.remove('active');
    this.activeIndex = nextIndex;
    if (activeSpan) activeSpan.classList.add('active');
  }
}

/**
 * Yield each text node that has POSITIVE-length overlap with the range,
 * along with its in-range [from, to) slice. The hard cases are
 * boundary-sharing nodes: e.g. when a triple-click extends into the
 * next paragraph (`endContainer = <P>, endOffset = 0`), the next
 * paragraph's first text node *starts at* the range end boundary but
 * has zero content inside the range. Range.intersectsNode would return
 * true for that node; we need to skip it.
 *
 * Solution: per-node range intersection via compareBoundaryPoints,
 * using both endpoints and strict inequalities. The check is:
 *   - if range.start >= nodeContents.end → node entirely before range
 *   - if range.end   <= nodeContents.start → node entirely after range
 * Both with non-strict `<=` / `>=` so a node that touches a range
 * boundary but doesn't actually contain in-range content is excluded.
 *
 * Verified against the triple-click case where boundary points sit on
 * <P> elements (offset 0 of the next P, etc.) and the previous P's
 * text node is the actual content the user means.
 */
function* textSegmentsInRange(range: Range): Generator<{ node: Text; from: number; to: number }> {
  const root = range.commonAncestorContainer;
  // Range wholly inside one text node.
  if (root.nodeType === Node.TEXT_NODE && range.startContainer === root && range.endContainer === root) {
    yield { node: root as Text, from: range.startOffset, to: range.endOffset };
    return;
  }
  const walkRoot = root.nodeType === Node.TEXT_NODE ? (root.parentNode ?? root) : root;
  const walker = document.createTreeWalker(walkRoot, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode() as Text | null;
  while (current) {
    const overlap = nodeOverlap(range, current);
    if (overlap) yield { node: current, from: overlap.from, to: overlap.to };
    current = walker.nextNode() as Text | null;
  }
}

function nodeOverlap(range: Range, node: Text): { from: number; to: number } | null {
  // Trivial case: range entirely within this single text node.
  if (node === range.startContainer && node === range.endContainer) {
    return { from: range.startOffset, to: range.endOffset };
  }
  let nodeRange: Range;
  try {
    nodeRange = document.createRange();
    nodeRange.selectNodeContents(node);
  } catch {
    return null;
  }
  // START_TO_END compares range.start to nodeRange.end.
  //   >= 0 means range.start is at or after node end → node entirely before range.
  if (range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0) return null;
  // END_TO_START compares range.end to nodeRange.start.
  //   <= 0 means range.end is at or before node start → node entirely after range.
  if (range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0) return null;
  // Some overlap. Slice using container/offset where applicable.
  const from = node === range.startContainer ? range.startOffset : 0;
  const to = node === range.endContainer ? range.endOffset : node.length;
  if (to <= from) return null;
  return { from, to };
}

function wrapRangeInWordSpans(range: Range, words: WordTiming[]): WrapResult {
  const empty: WrapResult = { spans: [], restore: () => {} };

  // Phase 1: gather text segments + build a cumulative "selected text"
  // string with a mapping from cumulative offset → (node, local offset).
  const segments: { node: Text; from: number; to: number; cumStart: number }[] = [];
  let cumPos = 0;
  let cumText = '';
  for (const seg of textSegmentsInRange(range)) {
    const slice = seg.node.data.slice(seg.from, seg.to);
    if (!slice) continue;
    segments.push({ ...seg, cumStart: cumPos });
    cumText += slice;
    cumPos += slice.length;
  }
  if (segments.length === 0 || cumText.length === 0) return empty;

  // Phase 2: for each SDK word, find its position in cumText + queue a
  // wrap on the right text node. Words that straddle a node boundary
  // (rare — usually an inline element breaks a word) are skipped: the
  // audio still plays, that word just isn't highlighted in-page.
  //
  // Gap-fill: when a sequence of SDK words doesn't appear in the page
  // (the canonical case: backend normaliser expanded "1917" → "un mil
  // naw cant un deg saith" or "Dr." → "Doctor"), we hold those indices
  // as `pendingUnmatched` and, when the next matched word lands, look
  // for a page-side token in the gap between the previous match's end
  // and the new match's start. If we find one (e.g. "1917", "Dr."),
  // we wrap that single token and map ALL the pending SDK indices to
  // it via fromIdx/toIdx. Result: the active-word highlight stays on
  // the page-side token for the full duration of its expansion instead
  // of freezing on the previous word.
  type WrapJob = {
    fromIdx: number;
    toIdx: number;
    localFrom: number;
    word: string;
    kind: 'word' | 'punctuation';
  };
  const perNodeWraps = new Map<Text, WrapJob[]>();

  const queueWrap = (cumPosFound: number, length: number, job: Omit<WrapJob, 'localFrom'>): boolean => {
    const end = cumPosFound + length;
    const seg = segments.find((s) => cumPosFound >= s.cumStart && end <= s.cumStart + (s.to - s.from));
    if (!seg) return false; // straddles a node boundary; drop silently
    const localFrom = seg.from + (cumPosFound - seg.cumStart);
    const queue = perNodeWraps.get(seg.node) ?? [];
    queue.push({ ...job, localFrom });
    perNodeWraps.set(seg.node, queue);
    return true;
  };

  let searchPos = 0;
  let pendingUnmatched: number[] = [];

  const drainPending = (gapStart: number, gapEnd: number): void => {
    if (pendingUnmatched.length === 0) return;
    const pending = pendingUnmatched;
    pendingUnmatched = [];
    const gapToks = findAllGapTokens(cumText, gapStart, gapEnd);
    if (gapToks.length === 0) return;

    // Distribute pending SDK indices across the wrappable gap tokens,
    // walking left-to-right. Each token claims ceil(remaining/remaining)
    // indices, so:
    //
    //   n >= k (more SDK indices than page tokens) — even-ish split with
    //     the earliest tokens carrying the rounding overhang. Eg
    //     "Dr. 1917" -> "Doctor un mil naw cant un deg saith" (n=7, k=2)
    //     puts 4 indices on "Dr." and 3 on "1917". Active-word marker
    //     moves through Dr., then 1917.
    //
    //   n < k (fewer SDK indices than page tokens) — earliest tokens get
    //     one index each, later tokens stay unwrapped. Eg "Doctor" (one
    //     SDK index) over page "Dr. blah" puts the index on "Dr." and
    //     leaves "blah" as plain text. Pre-fix this used floor-based
    //     partitioning which left the FIRST buckets empty and attached
    //     the lone index to a LATER gap token, jumping the highlight
    //     forward and never lighting up the earliest unmatched token.
    //     Codex on Llais#4 (correcting Llais#2's earlier even-split).
    const n = pending.length;
    const k = gapToks.length;
    let pendingIdx = 0;
    for (let g = 0; g < k && pendingIdx < n; g++) {
      const tok = gapToks[g];
      if (!tok) continue;
      const remaining = n - pendingIdx;
      const remainingTokens = k - g;
      const take = Math.ceil(remaining / remainingTokens);
      const from = pending[pendingIdx];
      const to = pending[pendingIdx + take - 1];
      if (from === undefined || to === undefined) continue;
      const wrapped = queueWrap(tok.pos, tok.length, {
        fromIdx: from,
        toIdx: to,
        word: cumText.slice(tok.pos, tok.pos + tok.length),
        kind: 'word',
      });
      // Only consume the slice if the wrap actually landed. When a gap
      // token straddles a text-node boundary, queueWrap returns false
      // and drops the wrap silently. Pre-fix we still advanced pendingIdx,
      // so the indices vanished even when a later gap token in the same
      // run was perfectly wrappable - the highlight then had nothing to
      // land on. Retrying the same slice on the next iteration uses the
      // recomputed `take` (remainingTokens decreased by 1) so the math
      // still terminates. Codex on Llais#6.
      if (wrapped) pendingIdx += take;
    }
  };

  for (let i = 0; i < words.length; i++) {
    const w = words[i];
    if (!w) continue;
    const cumPosFound = indexOfWord(cumText, w.word, searchPos);
    if (cumPosFound === -1) {
      // Don't decide yet — it might be an expanded digit/abbreviation
      // whose page-side token shows up in the gap before the next match.
      pendingUnmatched.push(i);
      continue;
    }
    // Drain pending: look for a wrappable page-side token in the gap
    // [searchPos, cumPosFound). If found, that one span covers every
    // pendingUnmatched SDK index. If not, those indices stay unrepresented
    // (same as the original behaviour — better than wrapping random
    // punctuation).
    drainPending(searchPos, cumPosFound);
    const wordEnd = cumPosFound + w.word.length;
    queueWrap(cumPosFound, w.word.length, {
      fromIdx: i,
      toIdx: i,
      word: w.word,
      kind: w.kind,
    });
    searchPos = wordEnd;
  }

  // Trailing pending: SDK kept emitting words after the last matched one
  // (e.g. selection ends with "...year 23" and Azure's "dau deg tri"
  // never had a follow-up match to trigger gap-drain). Same lookup
  // against the page tail.
  drainPending(searchPos, cumText.length);

  // Phase 3: apply wraps. Each node gets its content replaced by a
  // sequence of (text-before, span, text-between, span, ..., text-after)
  // nodes. We hold onto the original text node so cleanup can
  // re-insert it intact.
  const allSpans: HTMLSpanElement[] = [];
  const restoreRecords: Array<{
    parent: Node;
    originalNode: Text;
    insertedNodes: Node[];
    nextSibling: Node | null;
  }> = [];

  for (const [textNode, wraps] of perNodeWraps) {
    const parent = textNode.parentNode;
    if (!parent) continue;
    wraps.sort((a, b) => a.localFrom - b.localFrom);
    const nextSibling = textNode.nextSibling;
    const fullText = textNode.data;

    const inserted: Node[] = [];
    let cursor = 0;
    for (const wrap of wraps) {
      if (wrap.localFrom > cursor) {
        inserted.push(document.createTextNode(fullText.slice(cursor, wrap.localFrom)));
      }
      const literal = fullText.slice(wrap.localFrom, wrap.localFrom + wrap.word.length);
      const span = document.createElement('span');
      span.className = wrap.kind === 'punctuation' ? 'lw-p' : 'lw-w';
      // [from, to] is the inclusive range of SDK word indices this span
      // represents. Single-word spans have from == to. Group spans (one
      // page-side token covering many SDK words from an expansion) have
      // from < to.
      span.dataset.from = String(wrap.fromIdx);
      span.dataset.to = String(wrap.toIdx);
      span.textContent = literal;
      allSpans.push(span);
      inserted.push(span);
      cursor = wrap.localFrom + wrap.word.length;
    }
    if (cursor < fullText.length) {
      inserted.push(document.createTextNode(fullText.slice(cursor)));
    }

    parent.removeChild(textNode);
    for (const node of inserted) {
      if (nextSibling) parent.insertBefore(node, nextSibling);
      else parent.appendChild(node);
    }
    restoreRecords.push({ parent, originalNode: textNode, insertedNodes: inserted, nextSibling });
  }

  if (allSpans.length === 0) return empty;

  const restore = () => {
    for (const rec of restoreRecords) {
      for (const node of rec.insertedNodes) {
        if (node.parentNode === rec.parent) rec.parent.removeChild(node);
      }
      if (rec.nextSibling && rec.nextSibling.parentNode === rec.parent) {
        rec.parent.insertBefore(rec.originalNode, rec.nextSibling);
      } else {
        rec.parent.appendChild(rec.originalNode);
      }
    }
  };

  return { spans: allSpans, restore };
}

function indexOfWord(haystack: string, needle: string, from: number): number {
  // Try exact match first (cheap path). Fall back to case-insensitive
  // for things like sentence-initial capitalisation drift between
  // page text and SDK output.
  const exact = haystack.indexOf(needle, from);
  if (exact !== -1) return exact;
  const lowerH = haystack.toLowerCase();
  const lowerN = needle.toLowerCase();
  return lowerH.indexOf(lowerN, from);
}

/**
 * Find ALL "wrappable" page-side tokens in cumText[from, to). Used for
 * gap-fill: when the SDK emits a run of words that don't match anything
 * in the page (digit expansion, abbreviation expansion), one or more
 * original page tokens sit between the surrounding matched words. We
 * want to wrap EACH of them so the active-word highlight can move
 * through them as the audio progresses.
 *
 * Definition of "wrappable": a run of letters or digits, optionally
 * followed by a single period (so "Dr." matches as one token, and
 * "1917" matches without consuming a trailing comma). Unicode-aware
 * (\p{L}\p{N}) so Welsh accented chars (ŵ, ŷ, ô, â …) are kept whole.
 *
 * Returns the cumText-coordinate positions + lengths, or empty when the
 * gap has no word-like content (just whitespace/punctuation - leave the
 * unmatched SDK indices unrepresented rather than wrap a stray comma).
 *
 * Pre-fix this returned only the FIRST token, so "Dr. 1917" -> "Doctor
 * un mil naw cant un deg saith" mapped every SDK index onto "Dr." and
 * "1917" never got wrapped. Codex on Llais#2.
 */
function findAllGapTokens(cumText: string, from: number, to: number): { pos: number; length: number }[] {
  if (to <= from) return [];
  const out: { pos: number; length: number }[] = [];
  const re = /[\p{L}\p{N}]+\.?/gu;
  const slice = cumText.slice(from, to);
  let m: RegExpExecArray | null;
  while ((m = re.exec(slice)) !== null) {
    out.push({ pos: from + m.index, length: m[0].length });
  }
  return out;
}
